import React, { useContext, useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input } from 'semantic-ui-react';
import { useDebounce } from 'use-lodash-debounce'
import { RouteTypes } from '~/routes/constants';
import { MilkFixedPriceQuery } from '~/QueryHooks/MilkFixePriceQuery'
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import i18n from '~/components/i18n'
import 'dayjs/locale/de';
import { StyledTab } from '~/styles/table';
import TradingActivities from './TradingActivities';
import TableComponent from '~/components/TableComponent';
import { useNavigate } from 'react-router-dom';
import Dialog from '~/components/Dialog';
import MilkFixedService from '~/api/services/milkFixedPrice';
import { TermsWrapper } from '../Dashboard/style';
import { ThemeContext } from 'styled-components';
import { b64toBlob, handleErrors } from '~/utils/helpers';

interface IMilkFixedPrice { }

const getFarmerActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'milk-farmer-actions-show',
    link: RouteTypes.MILK_FIXED_PRICE_SHOW
  },
  {
    popupMessage: i18n.t('Offer amount'),
    type: 'icon',
    name: 'reply all',
    size: 'large',
    actionType: 'milk-farmer-actions-amount',
    link: RouteTypes.MILK_FIXED_PRICE_OFFER_AMOUNT,
    display: ['active']
  },
  {
    popupMessage: i18n.t('Results'),
    type: 'icon',
    name: 'clipboard outline',
    size: 'large',
    actionType: 'milk-farmer-actions-results',
    link: RouteTypes.MILK_FIXED_PRICE_RESULTS,
    display: ['active', 'completed', 'examination']
  }
]

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Trading slot ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Start/End'),
    key: 'dateFrom',
    type: 'startEnd',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const MilkFixedPrice: React.FunctionComponent<IMilkFixedPrice> = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    status: 'actual'
  })
  const farmerActions = getFarmerActions()
  const [acceptTermsRow, setAcceptTermsRow] = useState(null)
  const [link, setLink] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(0)
  const [loadingTerms, setLoadingTerms] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const [search, setSearch] = useState('')
  const { firstColor } = useContext(ThemeContext)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const navigate = useNavigate()
  const debouncedValue = useDebounce(search, 300)
  const [isBusy, setIsBusy] = useState(false)
  const [dataFilterActivities, setDataFilterActivities] = useState({
    page: 0,
    status: 'completed',
    rows: 10
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    isLoading: isBusyData,
    data: milkFixedData,
    isFetching: isFetchingData,
    refetch: fetchFilteredData
  } = MilkFixedPriceQuery.getMilkFixexPriceList({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: milkFixedOptions,
    refetch: refetchOptions,
    isLoading: isBusyPriceList
  } = MilkFixedPriceQuery.getMilkFixexPriceListOptions()

  const {
    data: tradingActivityOptions
  } = MilkFixedPriceQuery.getTradingActivityOptions()

  const {
    data: tradingSettings,
    isLoading: isBusySettings
  } = MilkFixedPriceQuery.getFarmerSettingsMilk()

  const {
    data: milkFixedCount,
    isLoading: isBusyPriceCount,
    refetch: fetchCount
  } = MilkFixedPriceQuery.getMilkFixexPriceCount({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: tradingActivitiesData,
    isLoading: isBusyTrading,
    isFetching: isFetchingTrading,
    refetch: fetchTradingActivities
  } = MilkFixedPriceQuery.getTradingActivities({ ...dataFilterActivities, page: dataFilterActivities.page === 0 ? dataFilterActivities.page : dataFilterActivities.page - 1 })

  useEffect(() => {
    dayjs.extend(LocalizedFormat)
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    refetchOptions()
    fetchFilteredData()
  }, [t])

  useEffect(() => {
    if (dataFilterActivities) {
      fetchTradingActivities()
    }
  }, [dataFilterActivities])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
      fetchCount()
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  function handleClickAction(row, type) {
    const action = farmerActions.find(action => action.actionType === type)
    const displayTradingRoom = Boolean(tradingSettings?.tradingRoom) && !row.termsAccepted && tradingSettings?.terms?.length > 0
    if (displayTradingRoom) {
      setAcceptTermsRow(row)
      setLink(action.link.replace(':id', row.id))
    } else {
      navigate(action.link.replace(':id', row.id))
    }
  }

  function renderContent() {
    return <TableComponent
      actions={farmerActions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      handleClickAction={handleClickAction}
      rows={milkFixedData?.rows || []}
      total={milkFixedData?.filtered || 0}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = milkFixedOptions.find((_, indexOption) => indexOption === activeIndex)
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      })
    }
  }

  function renderPanes() {
    const panes = milkFixedOptions.map(option => {
      const count = milkFixedCount.find(count => count.status === option.key)
      return {
        menuItem: `${option.label} (${count?.count || 0})`,
        render: () => renderContent()
      }
    })

    return <StyledTab
      menu={{ secondary: true, pointing: true }}
      panes={panes}
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => handleTabChange(data.activeIndex)}
    />
  }

  async function handleSubmitTradingTerms() {
    setLoadingTerms(true)
    try {
      await MilkFixedService.acceptTradingRooms(acceptTermsRow?.id)
      navigate(link)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingTerms(false)
  }

  async function handleClickTermItem(fileName) {
    setIsBusy(true)
    try {
      const response = await MilkFixedService.openTradingRoomFile({ fileName })
      const type = response.data.split(';')[0].split('data:')[1]
      const blob = b64toBlob(response.data, type);
      const blobUrl = URL.createObjectURL(blob);
      setFileSource(blobUrl);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderContenAcceptModal() {
    return <TermsWrapper firstColor={firstColor}>
      <div style={{ textAlign: 'center', marginBottom: 50 }} >
        <div className='note-title'>{t('Note')}</div>
        <div className='title'>{t('Please check the:')}</div>
        {tradingSettings?.terms.map(term => {
          return term.visible && <div
            onClick={() => handleClickTermItem(term.filename)}
            className='item' key={term.title}
          >
            {term.title}
          </div>
        })}
        <div className='title'>{t('which you can see by clicking on each.')}</div>
        <Checkbox
          label={<label className='highlight-label'>
            {tradingSettings?.texts?.find(text => text.slug === 'open_trading_slot_text')?.translation || ''}
          </label>}
          checked={acceptTerms === 1}
          onChange={(e, data) => setAcceptTerms(data.checked ? 1 : 0)}
          style={{ marginTop: 20 }}
        />
      </div>
    </TermsWrapper>
  }

  function renderAcceptModal() {
    return <Dialog
      isDialogOpen={Boolean(acceptTermsRow)}
      handleClose={() => {
        setAcceptTermsRow(null)
        setAcceptTerms(0)
        setLink('')
      }}
      isLoading={loadingTerms || isBusy}
      disableSubmit={acceptTerms !== 1}
      textHeader={t('Trading terms')}
      textSubmit={t('Enter trading')}
      textClose={t('Cancel process')}
      type={'dialog'}
      handleSubmit={() => handleSubmitTradingTerms()}
      textContent={renderContenAcceptModal()}
    />
  }

  function renderModalPreviewFile() {
    return (
      <Dialog
        open={Boolean(fileSource)}
        handleClose={() => setFileSource(null)}
        textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type={'preview'}
      />
    );
  }

  const loading = isBusyPriceCount || isBusyPriceList || isBusyData || isBusyTrading || isBusySettings

  return <Layout
    isBusy={loading}
    segmented
    title={t('Milk Fixed Price')}
    filters={renderSearch()}
  >

    {renderModalPreviewFile()}
    {acceptTermsRow && renderAcceptModal()}
    {milkFixedOptions && milkFixedCount && milkFixedData && renderPanes()}
    {tradingActivitiesData && !isFetchingData && tradingActivityOptions && <TradingActivities
      tradingActivities={tradingActivitiesData}
      isFetchingData={isFetchingTrading}
      tradingOptions={tradingActivityOptions}
      dataFilterActivities={dataFilterActivities}
      onChange={(value) => setDataFilterActivities(value)}
    />}
  </Layout>;
};

export default MilkFixedPrice;
